import React, { useEffect } from "react";
import "./BlogDetail.css";
import BlogData from "../BlogData";
import { useParams, useNavigate, Link } from "react-router-dom";
import { trackBlogView, trackPageView } from "../../../utils/analytics";
import SEOHead from "../../SEO/SEOHead";
import { FiArrowLeft } from "react-icons/fi";

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const article = BlogData.find((blog) => blog.id === parseInt(id));

  useEffect(() => {
    if (article) {
      const pageTitle = `${article.title} - Anime Review | ChibiDash Blog`;
      document.title = pageTitle;
      trackPageView(`/new-blog-home/article/${id}`, pageTitle);
      trackBlogView(id, article.title);
    }
  }, [id, article]);

  if (!article) {
    return <div>Article not found</div>;
  }

  return (
    <section className="blogdetail-section">
      <button className="blogdetail-back-button" onClick={() => navigate('/new-blog-home')}>
        <FiArrowLeft /> Back to Blog
      </button>
      <SEOHead
        title={`${article.title} - Anime Review | ChibiDash Blog`}
        description={article.sections?.[0]?.content?.substring(0, 160) || `Read ${article.title} on ChibiDash Blog`}
        path={`/new-blog-home/article/${id}`}
        type="article"
        keywords={`${article.title}, anime review, ${article.tags?.join(', ') || ''}, anime blog, ChibiDash`}
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.sections?.[0]?.content?.substring(0, 160),
          "datePublished": article.date,
          "publisher": { "@type": "Organization", "name": "ChibiDash", "url": "https://www.chibidash.com" },
          "mainEntityOfPage": `https://www.chibidash.com/new-blog-home/article/${id}`
        }}
      />
      {/* Header Section */}
      <div className="blogdetail-header">
        {/* Pre-title Image */}
        {article.preTitleImage && (
          <img
            src={article.preTitleImage}
            alt="Pre-title"
            className="blogdetail-pretitle-image"
          />
        )}
        <div className="blogdetail-title-container">
          <h1 className="blogdetail-title">{article.title}</h1>
        </div>
      </div>

      {/* Layout Section with Left and Right Parts */}
      <div className="blogdetail-layout">
        {/* Left Section with Main Blog Content */}
        <div className="blogdetail-main-content">
          {/* Main Image */}
          <img
            className="blogdetail-main-image"
            src={article.images[0]} // Main article image
            alt="Main Article"
          />

          {/* Tags and Date Section */}
          <div className="blogdetail-tags-date">
            <div className="blogdetail-tags">
              {article.tags.map((tag, index) => (
                <span key={index} className="blogdetail-tag">
                  {tag}
                </span>
              ))}
            </div>
            <p className="blogdetail-date">{article.date}</p>
          </div>

          {/* Render article sections dynamically */}
          {article.sections.map((section, index) => (
            <div key={index} className="blogdetail-section-content">
              {/* Pre-heading Images */}
              {section.preHeadingImages && (
                <div className="blogdetail-preheading-images">
                  {section.preHeadingImages.map((image, i) => (
                    <img
                      key={i}
                      src={image}
                      alt={`Pre-heading Image ${i + 1}`}
                      className="blogdetail-side-by-side-image"
                    />
                  ))}
                </div>
              )}

              <h2 className="blogdetail-section-title">{section.heading}</h2>

              {/* Pre-content Images */}
              {section.preContentImages && (
                <div className="blogdetail-precontent-images">
                  {section.preContentImages.map((image, i) => (
                    <img
                      key={i}
                      src={image}
                      alt={`Pre-content Image ${i + 1}`}
                      className="blogdetail-precontent-image"
                    />
                  ))}
                </div>
              )}

              <p className="blogdetail-section-text">{section.content}</p>

              {/* Post-content Images */}
              {section.postContentImages && (
                <div className="blogdetail-postcontent-images">
                  {section.postContentImages.map((image, i) => (
                    <img
                      key={i}
                      src={image}
                      alt={`Post-content Image ${i + 1}`}
                      className="blogdetail-postcontent-image"
                    />
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Inline Images */}
          <div className="blogdetail-inline-images">
            {article.images.slice(1).map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Inline ${index + 1}`}
                className="blogdetail-inline-image"
              />
            ))}
          </div>
        </div>

        {/* Right Section with Additional Posts */}
        <div className="blogdetail-sidebar">
          <h2 className="blogdetail-sidebar-heading">More Articles</h2>
          {BlogData.map(
            (related) =>
              related.id !== article.id && (
                <Link key={related.id} to={`/new-blog-home/article/${related.id}`} className="blogdetail-sidebar-post">
                  <img
                    className="blogdetail-sidebar-image"
                    src={related.images[0]}
                    alt={related.title}
                  />
                  <div className="blogdetail-sidebar-content">
                    <h3 className="blogdetail-sidebar-name">{related.title}</h3>
                    <span className="blogdetail-sidebar-viewmore">Read →</span>
                  </div>
                </Link>
              )
          )}
        </div>
      </div>

      {/* Related Articles Section */}
      <div className="blogdetail-related-articles">
        <h2 className="blogdetail-related-heading">You might also like</h2>
        <div className="blogdetail-related-items">
          {BlogData.map(
            (related) =>
              related.id !== article.id && (
                <Link
                  key={related.id}
                  to={`/new-blog-home/article/${related.id}`}
                  className="blogdetail-related-item"
                >
                  <img
                    className="blogdetail-related-image"
                    src={related.images[0]}
                    alt={related.title}
                  />
                  <h3 className="blogdetail-related-name">{related.title}</h3>
                  <div className="blogdetail-related-tags">
                    {related.tags.map((tag, i) => (
                      <span key={i} className="blogdetail-related-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </Link>
              )
          )}
        </div>
      </div>
    </section>
  );
};

export default BlogDetail;
